import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import type {
  InvoiceEmissionJob,
  InvoiceEmissionJobStatus,
  InvoiceJobsSummaryResponse,
} from "@repo/shared";
import { useApi } from "./useApi";
import { useCuits } from "./useCuits";

type InvoiceJobListItem = Omit<InvoiceEmissionJob, "items">;
type NotifyJobEnqueuedOptions = {
  openCenter?: boolean;
};

const JOB_CENTER_OPEN_KEY = "facturador.jobCenter.open";
const JOB_CENTER_COLLAPSED_KEY = "facturador.jobCenter.collapsed";
const RECENT_RETENTION_MS = 10 * 60 * 1000;
const HIGHLIGHT_MS = 6_000;

function isTerminalStatus(status: InvoiceEmissionJobStatus): boolean {
  return status === "success" || status === "partial" || status === "failed";
}

function isActiveStatus(status: InvoiceEmissionJobStatus): boolean {
  return status === "queued" || status === "waiting_lock" || status === "processing";
}

function isCreditNoteJob(kind: InvoiceEmissionJob["kind"]): boolean {
  return kind === "credit_note_batch_range";
}

function readBooleanFromStorage(key: string, fallback: boolean): boolean {
  if (typeof window === "undefined") return fallback;
  const raw = window.localStorage.getItem(key);
  if (raw === "true") return true;
  if (raw === "false") return false;
  return fallback;
}

function writeBooleanToStorage(key: string, value: boolean) {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(key, String(value));
}

function toMs(iso: string | null): number {
  if (!iso) return 0;
  const parsed = Date.parse(iso);
  return Number.isFinite(parsed) ? parsed : 0;
}

export function useInvoiceJobsList(
  cuitId: number | undefined,
  panelOpen: boolean,
  summaryActiveCount: number
) {
  const { authFetch } = useApi();

  return useQuery({
    queryKey: ["invoice-jobs", cuitId] as const,
    queryFn: async (): Promise<InvoiceJobListItem[]> => {
      return authFetch(`/api/cuits/${cuitId}/invoice-jobs`);
    },
    enabled: !!cuitId && (panelOpen || summaryActiveCount > 0),
    refetchInterval: (query) => {
      const jobs = query.state.data as InvoiceJobListItem[] | undefined;
      const hasActive = jobs?.some((job) => isActiveStatus(job.status)) ?? summaryActiveCount > 0;

      if (hasActive) return 2_000;
      if (panelOpen) return 10_000;
      return false;
    },
  });
}

export function useInvoiceJobsSummary(cuitId: number | undefined, enabled: boolean) {
  const { authFetch } = useApi();

  return useQuery({
    queryKey: ["invoice-jobs-summary", cuitId] as const,
    queryFn: async (): Promise<InvoiceJobsSummaryResponse> => {
      return authFetch(`/api/cuits/${cuitId}/invoice-jobs/summary`);
    },
    enabled: !!cuitId && enabled,
    refetchInterval: 45_000,
  });
}

export function useInvoiceJobDetails(
  cuitId: number | undefined,
  jobId: number | null,
  enabled = true
) {
  const { authFetch } = useApi();

  return useQuery({
    queryKey: ["invoice-job", cuitId, jobId] as const,
    queryFn: async (): Promise<InvoiceEmissionJob> => {
      return authFetch(`/api/cuits/${cuitId}/invoice-jobs/${jobId}`);
    },
    enabled: !!cuitId && !!jobId && enabled,
    refetchInterval: (query) => {
      const job = query.state.data as InvoiceEmissionJob | undefined;
      if (!job) return 2_000;
      if (isTerminalStatus(job.status)) return false;
      return 2_000;
    },
  });
}

type InvoiceJobCenterContextValue = {
  isOpen: boolean;
  isCollapsed: boolean;
  activeCount: number;
  jobs: InvoiceJobListItem[];
  activeJobs: InvoiceJobListItem[];
  recentJobs: InvoiceJobListItem[];
  selectedJobId: number | null;
  highlightedJobId: number | null;
  selectedJob: InvoiceEmissionJob | null;
  isLoading: boolean;
  setOpen: (open: boolean) => void;
  toggleCollapsed: () => void;
  selectJob: (jobId: number | null) => void;
  notifyJobEnqueued: (jobId: number, options?: NotifyJobEnqueuedOptions) => void;
  refetchJobs: () => void;
};

const InvoiceJobCenterContext = createContext<InvoiceJobCenterContextValue | null>(null);

export function InvoiceJobCenterProvider({ children }: { children: ReactNode }) {
  const { activeCuit } = useCuits();
  const queryClient = useQueryClient();

  const [isOpen, setIsOpen] = useState<boolean>(() =>
    readBooleanFromStorage(JOB_CENTER_OPEN_KEY, false)
  );
  const [isCollapsed, setIsCollapsed] = useState<boolean>(() =>
    readBooleanFromStorage(JOB_CENTER_COLLAPSED_KEY, false)
  );
  const [selectedJobId, setSelectedJobId] = useState<number | null>(null);
  const [highlightedJobId, setHighlightedJobId] = useState<number | null>(null);

  const lastStatusByJobRef = useRef<Map<number, InvoiceEmissionJobStatus>>(new Map());
  const highlightTimeoutRef = useRef<number | null>(null);

  const cuitId = activeCuit?.id;

  const jobsSummaryQuery = useInvoiceJobsSummary(cuitId, !isOpen);
  const summaryActiveCount = jobsSummaryQuery.data?.activeCount ?? 0;
  const jobsQuery = useInvoiceJobsList(cuitId, isOpen, summaryActiveCount);
  const selectedJobQuery = useInvoiceJobDetails(cuitId, selectedJobId, isOpen && selectedJobId !== null);

  useEffect(() => {
    writeBooleanToStorage(JOB_CENTER_OPEN_KEY, isOpen);
  }, [isOpen]);

  useEffect(() => {
    writeBooleanToStorage(JOB_CENTER_COLLAPSED_KEY, isCollapsed);
  }, [isCollapsed]);

  useEffect(() => {
    setSelectedJobId(null);
    setHighlightedJobId(null);
    lastStatusByJobRef.current.clear();
  }, [cuitId]);

  useEffect(() => {
    const jobsData = jobsQuery.data;
    if (!jobsData) return;

    const previous = lastStatusByJobRef.current;
    const next = new Map<number, InvoiceEmissionJobStatus>();

    for (const job of jobsData) {
      const prevStatus = previous.get(job.id);
      const currentStatus = job.status;

      if (prevStatus && !isTerminalStatus(prevStatus) && isTerminalStatus(currentStatus)) {
        if (cuitId) {
          queryClient.invalidateQueries({ queryKey: ["transfers", cuitId] });
          queryClient.invalidateQueries({ queryKey: ["invoices", cuitId] });
          queryClient.invalidateQueries({ queryKey: ["credit-notes", cuitId] });
          queryClient.invalidateQueries({ queryKey: ["invoice-jobs-summary", cuitId] });
        }

        const action = {
          label: "Ver detalle",
          onClick: () => {
            setIsOpen(true);
            setIsCollapsed(false);
            setSelectedJobId(job.id);
          },
        };

        const creditNoteJob = isCreditNoteJob(job.kind);

        if (currentStatus === "success") {
          toast.success(creditNoteJob ? "Notas de crédito completadas" : "Facturación completada", {
            description: `${job.successItems}/${job.totalItems} comprobantes emitidos`,
            action,
          });
        } else if (currentStatus === "partial") {
          toast.warning(creditNoteJob ? "Notas de crédito parciales" : "Facturación parcial", {
            description:
              job.errorMessage || `${job.successItems} emitidos, ${job.failedItems} con error`,
            action,
          });
        } else {
          toast.error(creditNoteJob ? "Emisión de NC fallida" : "Facturación fallida", {
            description: job.errorMessage || "No se pudo completar la emisión.",
            action,
          });
        }
      }

      next.set(job.id, currentStatus);
    }

    lastStatusByJobRef.current = next;
  }, [cuitId, jobsQuery.dataUpdatedAt, queryClient]);

  useEffect(() => {
    return () => {
      if (highlightTimeoutRef.current !== null && typeof window !== "undefined") {
        window.clearTimeout(highlightTimeoutRef.current);
      }
    };
  }, []);

  const jobs = jobsQuery.data ?? [];

  const activeJobs = useMemo(
    () => jobs.filter((job) => isActiveStatus(job.status)),
    [jobs]
  );

  const recentJobs = useMemo(
    () => {
      const nowMs = jobsQuery.dataUpdatedAt;
      if (!nowMs) return [];

      return jobs.filter((job) => {
        if (!isTerminalStatus(job.status)) return false;
        const updatedAtMs = toMs(job.updatedAt);
        return updatedAtMs > 0 && nowMs - updatedAtMs <= RECENT_RETENTION_MS;
      });
    },
    [jobs, jobsQuery.dataUpdatedAt]
  );

  const setOpen = useCallback((open: boolean) => {
    if (open && typeof document !== "undefined") {
      const activeElement = document.activeElement;
      if (activeElement instanceof HTMLElement) {
        activeElement.blur();
      }
    }
    setIsOpen(open);
    if (!open) setSelectedJobId(null);
  }, []);

  const toggleCollapsed = useCallback(() => {
    setIsCollapsed((prev) => !prev);
  }, []);

  const selectJob = useCallback((jobId: number | null) => {
    setSelectedJobId(jobId);
    if (jobId !== null) {
      setIsOpen(true);
      setIsCollapsed(false);
    }
  }, []);

  const notifyJobEnqueued = useCallback(
    (jobId: number, options?: NotifyJobEnqueuedOptions) => {
      const openCenter = options?.openCenter ?? true;

      if (openCenter) {
        setIsOpen(true);
        setIsCollapsed(false);
        setSelectedJobId(jobId);
      }
      setHighlightedJobId(jobId);

      toast.success("Emisión encolada", {
        description: `Job #${jobId} agregado a la cola.`,
        action: {
          label: "Ver progreso",
          onClick: () => {
            setIsOpen(true);
            setIsCollapsed(false);
            setSelectedJobId(jobId);
          },
        },
      });

      if (highlightTimeoutRef.current !== null && typeof window !== "undefined") {
        window.clearTimeout(highlightTimeoutRef.current);
      }
      if (typeof window !== "undefined") {
        highlightTimeoutRef.current = window.setTimeout(() => {
          setHighlightedJobId((current) => (current === jobId ? null : current));
        }, HIGHLIGHT_MS);
      }

      if (cuitId) {
        queryClient.invalidateQueries({ queryKey: ["invoice-jobs", cuitId] });
        queryClient.invalidateQueries({ queryKey: ["invoice-jobs-summary", cuitId] });
        queryClient.invalidateQueries({ queryKey: ["credit-notes", cuitId] });
      }
    },
    [cuitId, queryClient]
  );

  const refetchJobs = useCallback(() => {
    void jobsQuery.refetch();
  }, [jobsQuery]);

  const value = useMemo<InvoiceJobCenterContextValue>(
    () => ({
      isOpen,
      isCollapsed,
      activeCount: isOpen
        ? activeJobs.length
        : activeJobs.length > 0
          ? activeJobs.length
          : summaryActiveCount,
      jobs,
      activeJobs,
      recentJobs,
      selectedJobId,
      highlightedJobId,
      selectedJob: selectedJobQuery.data ?? null,
      isLoading: jobsQuery.isLoading || selectedJobQuery.isLoading || jobsSummaryQuery.isLoading,
      setOpen,
      toggleCollapsed,
      selectJob,
      notifyJobEnqueued,
      refetchJobs,
    }),
    [
      isOpen,
      isCollapsed,
      activeJobs.length,
      summaryActiveCount,
      jobs,
      activeJobs,
      recentJobs,
      selectedJobId,
      highlightedJobId,
      selectedJobQuery.data,
      jobsQuery.isLoading,
      selectedJobQuery.isLoading,
      jobsSummaryQuery.isLoading,
      setOpen,
      toggleCollapsed,
      selectJob,
      notifyJobEnqueued,
      refetchJobs,
    ]
  );

  return (
    <InvoiceJobCenterContext.Provider value={value}>{children}</InvoiceJobCenterContext.Provider>
  );
}

export function useInvoiceJobCenter() {
  const context = useContext(InvoiceJobCenterContext);
  if (!context) {
    throw new Error("useInvoiceJobCenter must be used within InvoiceJobCenterProvider");
  }
  return context;
}
